/**
 * AUDITED VERBATIM MLS COMPLIANCE STRINGS — do not edit without compliance
 * sign-off. MLS auditors string-match these character-for-character against
 * the rule text.
 *
 * Sources: MLS GRID IDX Rules (2026-07-15) · Triad MLS R&R §18.2 · Doorify
 * MLS R&R (2026-05-26) §§12.4, 17.8, 17.10 · FMLS R&R (2025-03-17) Rules 11,
 * 13 · GAMLS R&R (R08012024) §12.
 *
 * Every string here is duplicated literally in MlsAttribution.test.tsx and
 * asserted with .toBe() — changing one requires consciously editing BOTH
 * files. `jest -u` cannot bless drift. That is deliberate.
 *
 * Rendering rules (see MlsAttribution.tsx):
 *  - each string renders as a single JSX expression, never as JSX text that
 *    Prettier can rewrap, never through i18n, never concatenated;
 *  - the only permitted substitutions are `{Token}` slots and `[text](url)`
 *    link markup; neither the token braces nor the link markup is part of
 *    the protected visible text;
 *  - a string with a token that cannot be resolved is omitted whole, never
 *    rendered mutated ("as of Invalid date" is a violation, absence is not).
 *
 * `TriangleMLS` is the feed_id; "Doorify" is that MLS's current display
 * brand. The display strings below say Doorify while every backend
 * identifier — feed_id, config key, DB value — deliberately stays
 * TriangleMLS.
 */

/**
 * MLS GRID Rule 24, as the two frozen segments either side of the
 * {DataTimestamp} slot. Module-level rather than namespaced under a feed:
 * every MLS GRID redistributor owes this same sentence, and Canopy is only
 * the first one we carry.
 */
export const MLS_GRID_RULE_24 = Object.freeze({
  before: 'Based on information submitted to the MLS GRID as of ',
  after:
    '. All data is obtained from various sources and may not have been verified by broker or MLS GRID. Supplied Open House Information is subject to change without notice. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.',
})

const GRID_RULE_24_DISCLAIMER = `${MLS_GRID_RULE_24.before}{DataTimestamp}${MLS_GRID_RULE_24.after}`

export const MLS_STRINGS = Object.freeze({
  CanopyMLS: Object.freeze({
    officeSuffix: null,
    // MLS GRID Rule 23
    sourceLine: 'Listings courtesy of Canopy MLS as distributed by MLS GRID',
    copyright: null,
    // MLS GRID Rule 24 VERBATIM
    disclaimers: Object.freeze([GRID_RULE_24_DISCLAIMER]),
    // MLS GRID Rule 22 — sold-status alternative to naming the cooperating
    // brokerage (which the details payload does not carry).
    soldNotice:
      'Properties displayed may be listed or sold by various participants in the MLS',
  }),
  TriadMLS: Object.freeze({
    // 18.2 (K)
    officeSuffix: ', licensed in NC',
    // 18.2 (F) VERBATIM
    sourceLine:
      "Properties marked with the Triad MLS, Inc. icon are provided courtesy of the Triad MLS, Inc.'s, Internet Data Exchange Database.",
    // 18.2 (H). The published rule text says "Copyright"; ACR-23 standardises
    // on "©" across every feed and treats the difference as immaterial.
    copyright: '© {CurrentYear} Triad MLS, Inc. All rights reserved.',
    // 18.2 (I) VERBATIM
    disclaimers: Object.freeze([
      'Information Deemed Reliable But Not Guaranteed',
    ]),
  }),
  TriangleMLS: Object.freeze({
    officeSuffix: null,
    // Doorify 17.10 VERBATIM
    sourceLine:
      'Listings marked with an icon are provided courtesy of the Doorify MLS, Inc. of North Carolina, Internet Data Exchange Database.',
    // Doorify 12.4 VERBATIM — "©" or "Copyright" only; "(c)" is prohibited.
    copyright:
      '© {CurrentYear} Doorify MLS, Inc. of North Carolina. All rights reserved.',
    // Doorify 17.10 VERBATIM (short option)
    disclaimers: Object.freeze(['Information Not Guaranteed']),
  }),
  FMLS: Object.freeze({
    officeSuffix: null,
    // Acre-authored
    sourceLine: 'Listings courtesy of First MLS (FMLS)',
    // FMLS 11.9 VERBATIM
    copyright: '© {CurrentYear} First MLS',
    disclaimers: Object.freeze([
      // FMLS 11.10 VERBATIM
      'Listing content is believed accurate but not guaranteed.',
      // FMLS 13.7(e)(2) VERBATIM — the link target is FMLS's own DMCA page.
      'Listings on this website come from the FMLS IDX Compilation and may be held by brokerage firms other than the owner of this website. The listing brokerage is identified in any listing details. Information is deemed reliable but is not guaranteed. If you believe any FMLS Listing contains material that infringes your copyrighted work, please [click here](https://www.fmls.com/dmca.htm) to review our DMCA policy and learn how to submit a takedown request. © {CurrentYear} FMLS.',
    ]),
  }),
  GeorgiaMLS: Object.freeze({
    officeSuffix: null,
    // GAMLS §12(e)(j)
    sourceLine: 'Source: Georgia MLS • Last updated {DataTimestamp}',
    copyright: null,
    disclaimers: Object.freeze([]),
  }),
} as const)
